import type { PlayerInspectionResult } from '@/types';

/**
 * One CSV summarising the whole inspection: the risk signals, then the per-game
 * and per-currency breakdowns, then per-day activity. A single file support can
 * attach to a case.
 *
 * ⚠️ Breakdown rows keep their NATIVE currency (a bet happened in one currency),
 * so every money row carries its currency column — a mixed-currency SUM() would
 * otherwise look authoritative and be nonsense. Only the risk block's totals are
 * in the converted base unit, and it is labelled.
 */
export function toCsv(r: PlayerInspectionResult): string {
  const rows: string[][] = [];
  const push = (...cells: (string | number)[]) => rows.push(cells.map((c) => String(c)));

  push('section', 'a', 'b', 'c', 'd', 'e', 'f');
  push('player', r.overview.play_service_id, r.overview.player_id);
  push(
    'risk',
    'score',
    String(r.risk.score),
    'level',
    r.risk.level,
    'NOTE',
    'heuristic aid, not a verdict',
  );
  for (const s of r.risk.signals) push('signal', s.severity, s.key, s.title, s.evidence);

  push('');
  push('per_game', 'game_uuid', 'currency', 'bet', 'win', 'ggr', 'bets');
  for (const g of r.perGame) push('game', g.key, g.currency ?? '', g.bet, g.win, g.ggr, g.bets);

  push('');
  push('per_currency', 'currency', 'bet', 'win', 'ggr', 'bets');
  for (const c of r.perCurrency) push('currency', c.key, c.bet, c.win, c.ggr, c.bets);

  push('');
  push('per_day_utc', 'day', 'bet', 'win', 'ggr', 'bets', `unit=${r.unit}`);
  for (const d of r.perDay) push('day', d.day, d.bet, d.win, d.ggr, d.bets);

  push('');
  push('ip', 'address', 'kind', 'hits', 'other_accounts');
  for (const ip of [...r.ipLinks.public, ...r.ipLinks.proxy])
    push('ip', ip.ip, ip.kind, ip.hits, ip.others);

  return rows.map((cells) => cells.map(escapeCell).join(',')).join('\n');
}

function escapeCell(value: string): string {
  const v = value ?? '';
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Named by client + player, sanitised so a name can't inject a path. */
export function inspectionFilename(client: string, playerId: string, from: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 40);
  return `player-inspection_${safe(client)}_${safe(playerId)}_${safe(from.slice(0, 10))}.csv`;
}
